import { Injectable } from '@angular/core';
import { IChartData, InitialData, TimeInMs } from './types';

@Injectable({
  providedIn: 'root'
})
export class DataEnrichmentService {
  processData(entryData: InitialData[], granularity: number): InitialData[] {
    const updatedData: InitialData[] = [];

    for (const entry of entryData) {
      updatedData.push({
        chart: entry.chart,
        data: this._handleIsolatedPoints(entry.data, granularity)
      });
    }

    return updatedData;
  }

  _handleIsolatedPoints(initialData: IChartData[], granularity: number): IChartData[] {
    const dataChart = initialData.filter((value, index) => {
      return initialData.map(data => data.x).indexOf(value.x) === index;
    });

    const updatedData: IChartData[] = [];
    const gapSize = this._getGapSizeFromGranularity(granularity);

    if (!dataChart.length) {
      return null;
    }

    if (dataChart.length === 1) {
      if (dataChart[0].failureCodes) {
        updatedData.push({
          x: dataChart[0].x,
          y: dataChart[0].y ?? 0,
          failureCodes: dataChart[0].failureCodes,
          marker: {
            enabled: true,
            fillColor: 'red',
            symbol: dataChart[0]?.marker?.symbol || 'circle'
          }
        });
      } else {
        updatedData.push({
          x: dataChart[0].x,
          y: dataChart[0].y,
          marker: {
            enabled: true
          },
          additionalTooltip: dataChart[0]?.additionalTooltip
        });
      }
      return updatedData;
    }

    //first point (in highcharts data is always the timestamp)
    if (typeof dataChart[0].failureCodes === 'string') {
      updatedData.push({
        x: dataChart[0].x,
        y: dataChart[0].y,
        failureCodes: dataChart[0].failureCodes,
        marker: {
          enabled: true,
          fillColor: 'red',
          symbol: dataChart[0]?.marker?.symbol || 'circle'
        }
      });

      updatedData.push({
        x: dataChart[0].x,
        y: null,
        failureCodes: dataChart[0].failureCodes,
        marker: {
          enabled: false,
          fillColor: 'red',
          symbol: dataChart[0]?.marker?.symbol || 'circle'
        }
      });
    } else {
      updatedData.push({
        x: dataChart[0].x,
        y: dataChart[0].y,
        marker: {
          enabled: !!dataChart[1].failureCodes
        },
        additionalTooltip: dataChart[0]?.additionalTooltip
      });
    }

    //regular points
    for (let i = 1; i < dataChart.length - 1; i++) {
      if (typeof dataChart[i].failureCodes === 'string') {
        if (dataChart[i - 1].failureCodes !== null) {
          updatedData.push({
            x: dataChart[i - 1].x,
            y: null,
            failureCodes: dataChart[i].failureCodes,
            marker: {
              enabled: false,
              fillColor: 'red',
              symbol: dataChart[i]?.marker?.symbol || 'circle'
            },
            additionalTooltip: dataChart[i]?.additionalTooltip
          });
        }

        updatedData.push({
          x: dataChart[i].x,
          y: 0,
          failureCodes: dataChart[i].failureCodes,
          marker: {
            enabled: true,
            fillColor: 'red',
            symbol: dataChart[i]?.marker?.symbol || 'circle'
          },
          additionalTooltip: dataChart[i]?.additionalTooltip
        });

        updatedData.push({
          x: dataChart[i].x,
          y: null,
          failureCodes: dataChart[i].failureCodes,
          marker: {
            enabled: false,
            fillColor: 'red',
            symbol: dataChart[i]?.marker?.symbol || 'circle'
          },
          additionalTooltip: dataChart[i]?.additionalTooltip
        });
      } else {
        updatedData.push({
          x: dataChart[i].x,
          y: dataChart[i].y,
          marker: {
            enabled: !!(
              dataChart[i - 1].failureCodes !== null &&
              dataChart[i + 1].failureCodes !== null &&
              dataChart[i + 1].failureCodes
            )
          },
          additionalTooltip: dataChart[i]?.additionalTooltip
        });
      }
    }

    //last point
    if (typeof dataChart[dataChart.length - 1].failureCodes === 'string') {
      if (dataChart[dataChart.length - 2].failureCodes === null) {
        updatedData.push({
          x: dataChart[dataChart.length - 2].x,
          y: null,
          failureCodes: dataChart[dataChart.length - 1].failureCodes,
          marker: {
            enabled: false,
            fillColor: 'red',
            symbol: dataChart[dataChart.length - 1]?.marker?.symbol || 'circle'
          }
        });
      }

      updatedData.push({
        x: dataChart[dataChart.length - 1].x,
        y: dataChart[dataChart.length - 1].y,
        failureCodes: dataChart[dataChart.length - 1].failureCodes,
        marker: {
          enabled: true,
          fillColor: 'red',
          symbol: dataChart[dataChart.length - 1]?.marker?.symbol || 'circle'
        },
        additionalTooltip: dataChart[dataChart.length - 1]?.additionalTooltip
      });
    } else {
      updatedData.push({
        x: dataChart[dataChart.length - 1].x,
        y: dataChart[dataChart.length - 1].y,
        marker: {
          enabled: !!dataChart[dataChart.length - 2].failureCodes
        },
        additionalTooltip: dataChart[dataChart.length - 1]?.additionalTooltip
      });
    }

    return updatedData;
  }

  private _getGapSizeFromGranularity(granularity: number): number {
    if (!granularity) {
      return 1;
    }

    if (granularity === 1440) {
      return TimeInMs.Day;
    }

    if (granularity === 720) {
      return TimeInMs.TwelveHours;
    }

    if (granularity === 60) {
      return TimeInMs.Hour;
    }

    if (granularity === 15) {
      return TimeInMs.QuarterHour;
    }

    return TimeInMs.Minute;
  }
}
